import type { KeySignature, KeySignatureDefinition, PitchClass, Accidental } from '../types/music';

/**
 * Key signature definitions with their sharps and flats
 */
export const KEY_SIGNATURES: Record<KeySignature, KeySignatureDefinition> = {
  'C': {
    key: 'C',
    label: 'C Major (no sharps/flats)',
    sharps: [],
    flats: []
  },
  'G': {
    key: 'G',
    label: 'G Major (F#)',
    sharps: ['F'],
    flats: []
  },
  'D': {
    key: 'D',
    label: 'D Major (F#, C#)',
    sharps: ['F', 'C'],
    flats: []
  },
  'A': {
    key: 'A',
    label: 'A Major (F#, C#, G#)',
    sharps: ['F', 'C', 'G'],
    flats: []
  },
  'F': {
    key: 'F',
    label: 'F Major (Bb)',
    sharps: [],
    flats: ['B']
  },
  'Bb': {
    key: 'Bb',
    label: 'Bb Major (Bb, Eb)',
    sharps: [],
    flats: ['B', 'E']
  },
  'Eb': {
    key: 'Eb',
    label: 'Eb Major (Bb, Eb, Ab)',
    sharps: [],
    flats: ['B', 'E', 'A']
  }
};

/**
 * Get the accidental for a pitch class in a given key signature
 */
export function getAccidentalInKey(pitchClass: PitchClass, keySignature: KeySignature): Accidental {
  const keyDef = KEY_SIGNATURES[keySignature];

  if (keyDef.sharps.includes(pitchClass)) {
    return 'sharp';
  }
  if (keyDef.flats.includes(pitchClass)) {
    return 'flat';
  }
  return 'natural';
}

/**
 * Check if a note input matches the expected note in a given key
 */
export function isCorrectNoteInKey(
  input: { pitchClass: PitchClass; accidental: Accidental },
  expected: { pitchClass: PitchClass; accidental: Accidental },
  _keySignature: KeySignature // Reserved for future key-aware comparison
): boolean {
  // Pitch class must always match
  if (input.pitchClass !== expected.pitchClass) {
    return false;
  }

  // Get the expected accidental based on key signature
  const expectedAccidental = expected.accidental;

  // Compare accidentals
  return input.accidental === expectedAccidental;
}

/**
 * Get display name for an accidental
 */
export function getAccidentalSymbol(accidental: Accidental): string {
  switch (accidental) {
    case 'sharp': return '♯';
    case 'flat': return '♭';
    case 'natural': return '';
  }
}

/**
 * Get short accidental symbol for display
 */
export function getAccidentalShortSymbol(accidental: Accidental): string {
  switch (accidental) {
    case 'sharp': return '#';
    case 'flat': return 'b';
    case 'natural': return '';
  }
}

/**
 * Available key signature options for the settings UI
 */
export const KEY_SIGNATURE_OPTIONS: { value: KeySignature; label: string }[] = [
  { value: 'C', label: 'C Major (no sharps/flats)' },
  { value: 'G', label: 'G Major (F♯)' },
  { value: 'D', label: 'D Major (F♯, C♯)' },
  { value: 'A', label: 'A Major (F♯, C♯, G♯)' },
  { value: 'F', label: 'F Major (B♭)' },
  { value: 'Bb', label: 'B♭ Major (B♭, E♭)' },
  { value: 'Eb', label: 'E♭ Major (B♭, E♭, A♭)' }
];
